<template>
  <div>
    <div id="header" class="bg-primary flex px-8 py-6">
      <Avatar class="mr-6" :src="avatar" :size="140" />
      <div class="flex flex-col justify-evenly items-start">
        <div>
          <FontAwesomeIcon icon="user" class="mr-3.5 text-secondary" />
          <span class="font-semibold">{{ fullName }}</span>
        </div>
        <div>
          <FontAwesomeIcon icon="envelope-open" class="mr-3.5 text-secondary" />
          <span class="underline">{{ email }}</span>
        </div>
        <div>
          <FontAwesomeIcon icon="mobile" class="mr-3.5 text-secondary" />
          <a :href="`tel:${phone}`">
            {{ phone }}
            <span class="text-xs">
              (click to call)
            </span>
          </a>
        </div>
      </div>
      <FontAwesomeIcon
        :icon="['far', 'times-circle']"
        size="2x"
        class="absolute cursor-pointer text-secondary top-4 right-0 mr-3.5"
        @click="closeModal"
      />
      <div
        v-if="!allowEditing"
        class="absolute cursor-pointer top-14 right-0 mr-3.5 bg-transparent px-2 py-1 rounded-full"
        style="box-shadow: inset 0px 0px 0px 3px var(--color-secondary);"
        @click="allowEditing = !allowEditing"
      >
        <FontAwesomeIcon :icon="['fas', 'pen']" class="text-secondary " />
      </div>
      <div
        v-else
        class="absolute cursor-pointer top-14 right-0 mr-3.5 bg-transparent px-2 py-1 rounded-full"
        style="box-shadow: inset 0px 0px 0px 3px var(--color-secondary);"
        @click="allowEditing = !allowEditing"
      >
        <FontAwesomeIcon :icon="['far', 'save']" class="text-secondary " />
      </div>
    </div>
    <div class="px-10 py-5">
      <div class="h-14 mb-6 flex justify-between">
        <Button icon="list" outline @click="goToMealPlanner">Meal Plan</Button>
        <Button outline class="flex items-center" @click="handleAppointment">
          <div class="flex justify-center items-center">
            <FontAwesomeIcon icon="calendar-alt" class="mr-4" />
            <div class="flex flex-col">
              <span style="font-size: 1rem;">Appointment</span>
              <span class="text-xs text-gray-400 capitalize">
                {{ nextAppointment }}
              </span>
            </div>
          </div>
        </Button>
        <Button icon="comments" outline>Message</Button>
      </div>
      <div class="firstBunch">
        <span>Gender</span>
        <span>:</span>
        <span>{{ gender }}</span>
        <span>Age</span>
        <span>:</span>
        <span>{{ age }}</span>
        <span>Height</span>
        <span>:</span>
        <span>{{ height }}</span>
        <span>Weight</span>
        <span>:</span>
        <span>{{ currentWeight }}</span>
      </div>
      <div class="w-full h-52 my-5">
        <WeightChart
          id="weight-chart"
          class="h-full"
          :chart-data="chartData"
          :chart-labels="chartLabels"
        />
      </div>
      <div class="secondBunch">
        <span>Type of diet</span>
        <span>:</span>
        <HorizontalList
          :allow-editing="allowEditing"
          :client="client"
          type="diets"
          :items="diets"
          @delete="removeItem"
        />
        <span>Intolerances</span>
        <span>:</span>
        <HorizontalList
          :allow-editing="allowEditing"
          :client="client"
          type="intolerances"
          :items="intolerances"
          @delete="removeItem"
        />
        <span>Allergies</span>
        <span>:</span>
        <HorizontalList
          :allow-editing="allowEditing"
          :client="client"
          type="allergies"
          :items="allergies"
          @delete="removeItem"
        />
        <span>Notes</span>
        <span>:</span>
      </div>
      <Editable
        v-model="notes"
        :allow-editing="allowEditing"
        :key="renderComponent"
      />
    </div>
  </div>
</template>

<script>
import HorizontalList from "@/pages/MyClients/components/HorizontalList";
import moment from "moment";
import * as measurementsKit from "@/utils/measurementConversions";
import { mapGetters } from "vuex";
import WeightChart from "@/components/WeightChart";
import MyClientAppointmentModal from "@/pages/MyClients/components/MyClientAppointmentModal";
import Editable from "@/pages/MyClients/components/Editable";

export default {
  name: "MyClientProfileModal",
  components: {
    HorizontalList,
    WeightChart,
    Editable
  },
  props: {
    client: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    fullName() {
      return `${this.client.name} ${this.client.surname}`;
    },
    email() {
      return this.client.email;
    },
    phone() {
      return this.client.phone;
    },
    avatar() {
      return this.client.image;
    },
    arrangedAppointment() {
      return this.client.next_appointment;
    },
    nextAppointment() {
      if (!this.arrangedAppointment.time) return "(Not arranged)";

      const calendarFormat = moment
        .unix(this.client.next_appointment.time)
        .calendar(null, {
          sameDay: "[Today] - h:mma",
          nextDay: "[Tomorrow] - h:mma",
          nextWeek: "ddd - h:mma"
        });

      return calendarFormat;
    },
    gender() {
      return this.client.gender;
    },
    age() {
      return this.client.age;
    },
    height() {
      const mode = measurementsKit.types.LENGTH;
      return measurementsKit.formatMeasurement(
        mode,
        this.nutritionistInfo.measurement_system,
        this.client.height
      );
    },
    currentWeight() {
      const mode = measurementsKit.types.WEIGHT;
      return measurementsKit.formatMeasurement(
        mode,
        this.nutritionistInfo.measurement_system,
        this.client.current_weight
      );
    },
    chartData() {
      const measurements = [
        91000,
        90000,
        87000,
        91000,
        92000,
        89000,
        87000,
        83000,
        69000
      ];
      const duration = 15;
      const goal = measurements[measurements.length - 1];
      const lastMeasurement = measurements[measurements.length - 2];
      const weightDifferenceTillGoal = lastMeasurement - goal;
      const remainingDay = duration - measurements.length + 1;
      const step = weightDifferenceTillGoal / remainingDay;
      let newArray = measurements.splice(0, measurements.length - 1);

      for (let i = 1; i <= remainingDay - 1; i++) {
        newArray.push((lastMeasurement - step * i).toFixed(1));
      }
      newArray.push(goal);

      const mode = measurementsKit.types.WEIGHT;

      newArray = newArray.map(dataPoint => {
        return parseInt(
          measurementsKit.formatMeasurement(
            mode,
            this.nutritionistInfo.measurement_system,
            dataPoint
          )
        );
      });

      return newArray;
    },
    chartLabels() {
      const measurements = [91, 90, 87, 91, 92, 89, 87, 83, 69];

      return this.chartData.map((_, index) => {
        if (index === 0) {
          return "Start";
        } else if (index === this.chartData.length - 1) {
          return "Goal";
        } else if (index === measurements.length - 2) {
          return "Now";
        }

        return `Week ${index}`;
      });
    },
    diets() {
      return this.client.diets;
    },
    intolerances() {
      return this.client.intolerances;
    },
    allergies() {
      return this.client.allergies;
    },
    notes: {
      set(newValue) {
        this.client.notes = newValue;
      },
      get() {
        return this.client.notes;
      }
    },
    ...mapGetters({ nutritionistInfo: "getNutritionistInfo" })
  },
  data() {
    return {
      allowEditing: false,
      renderComponent: 0
    };
  },
  methods: {
    removeItem(item) {
      this.client[item.type] = this.client[item.type].filter(
        asdf => item.item !== asdf
      );
    },
    closeModal() {
      if (
        !this.allowEditing ||
        confirm(
          "Are you sure that you want to close the modal? There might be unsaved changes."
        )
      ) {
        this.$modal.hide("MyClientProfileModal");
      }
    },
    goToMealPlanner() {
      this.$router.push({
        name: "MealPlanner",
        params: {
          clientId: this.client.id
        }
      });
      this.$modal.hideAll();
    },
    handleAppointment() {
      if (!this.arrangedAppointment.time) {
        // alert();
        this.$router.push({ name: "MyCalendar" });
        this.$modal.hideAll();
        return;
      }
      const { arrangedAppointment } = this;
      // show modal to view/cancel/reschedule appointment
      this.$modal.show(
        MyClientAppointmentModal,
        {
          appointment: {
            ...arrangedAppointment,
            client: this.client
          }
        },
        {
          name: "MyClientAppointmentModal",
          classes: "MyClientAppointmentModal rounded-xl overscroll-contain",
          height: "auto",
          maxWidth: 600,
          scrollable: true,
          adaptive: true
        }
      );
    }
  },
  created() {},
  watch: {
    allowEditing() {
      this.renderComponent += 1;
    }
  }
};
</script>

<style lang="postcss">
.MyClientProfileModal {
  position: fixed;
  overflow-y: scroll;
  right: 0px;
  left: auto !important;

  & .firstBunch {
    display: grid;
    grid-template-columns: 4.625rem 1.563rem auto;
    grid-row-gap: 1.25rem;
  }

  & .secondBunch {
    display: grid;
    grid-template-columns: 7.063rem 1.563rem auto;
    grid-row-gap: 1.25rem;
  }

  & #header {
    position: -webkit-sticky;
    position: sticky;
    top: 0px;
    z-index: 999;
  }
}
</style>
